"use client";

import { cn } from "@/lib/utils";
import { countDigitsBeforeIndex, formatDateDmY, normalizeDateInputDmY, parseDateDmY, selectionIndexAfterDigitCount } from "@/lib/utils/date-format";
import { resolveRelationshipId } from "@/lib/utils/theme-configuration-class";
import { Field } from "@base-ui/react/field";
import { Input } from "@base-ui/react/input";
import { Popover } from "@base-ui/react/popover";
import { Calendar as CalendarIcon } from "lucide-react";
import { useEffect, useId, useMemo, useState, type ChangeEvent } from "react";
import type { FormVariant } from "@/lib/types/form/form";
import Description from "../description";
import FieldError from "../field-error";
import Label from "../label";
import dynamic from "next/dynamic";
import "./style.scss";

const Calendar = dynamic(() => import("@/components/frontend/ui/calendar").then((module) => module.Calendar), { ssr: false });

const defaultStartMonth = new Date(1900, 0, 1);

function defaultEndMonth(): Date {
	const y = new Date().getFullYear();
	return new Date(y + 50, 11, 31);
}

export interface DatePickerInputProps {
	id?: string;
	name?: string;
	label?: string;
	hideLabel?: boolean;
	description?: string | null;
	error?: string;
	placeholder?: string;
	required?: boolean;
	disabled?: boolean;
	value?: string;
	onChange?: (value: string) => void;
	onBlur?: () => void;
	containerClassName?: string;
	autocomplete?: string | null;
	monthYearDropdown?: boolean | null;
	variant?: FormVariant;
}

export function DatePickerInput({
	id: idProp,
	name,
	label,
	hideLabel,
	description,
	error,
	placeholder,
	required,
	disabled,
	value = "",
	onChange,
	onBlur,
	containerClassName,
	autocomplete,
	monthYearDropdown = false,
	variant,
}: DatePickerInputProps) {
	const generatedId = useId();
	const id = idProp ?? generatedId;
	const [open, setOpen] = useState(false);
	const selected = useMemo(() => parseDateDmY(value ?? ""), [value]);
	const [month, setMonth] = useState<Date>(() => selected ?? new Date());

	useEffect(() => {
		if (selected) setMonth(selected);
	}, [selected]);

	const formVariantClassName = useMemo(() => `ms-form-${resolveRelationshipId(variant)}`, [variant]);
	const showDropdowns = Boolean(monthYearDropdown);
	const monthBounds = useMemo(() => (showDropdowns ? { start: defaultStartMonth, end: defaultEndMonth() } : null), [showDropdowns]);

	const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		const el = e.target;
		const raw = el.value;
		const caret = el.selectionStart ?? raw.length;
		const digitsBeforeCaret = countDigitsBeforeIndex(raw, caret);
		const next = normalizeDateInputDmY(raw);
		onChange?.(next);
		queueMicrotask(() => {
			const node = document.getElementById(id) as HTMLInputElement | null;
			if (!node || document.activeElement !== node) return;
			const pos = selectionIndexAfterDigitCount(next, digitsBeforeCaret);
			node.setSelectionRange(pos, pos);
		});
	};

	return (
		<Field.Root className={cn("flex flex-col items-start gap-2", containerClassName)}>
			{label && (
				<Label htmlFor={id} required={required} visible={!hideLabel}>
					{label}
				</Label>
			)}

			<Popover.Root
				open={open}
				onOpenChange={(next) => {
					setOpen(next);
					if (!next) onBlur?.();
				}}
			>
				<div className="flex w-full min-w-0">
					<Input
						id={id}
						name={name}
						type="text"
						inputMode="numeric"
						autoComplete={autocomplete ?? undefined}
						disabled={disabled}
						aria-invalid={error ? true : undefined}
						aria-expanded={open}
						placeholder={placeholder}
						value={value}
						onChange={handleInputChange}
						onKeyDown={(e) => {
							if (e.key === "ArrowDown") {
								e.preventDefault();
								setOpen(true);
							}
						}}
						className={cn(
							"ms-input min-w-0 flex-1 rounded-r-none border-r-0 text-base",
							"focus:z-[1] focus:outline focus:outline-2 focus:-outline-offset-1",
						)}
					/>
					<Popover.Trigger
						disabled={disabled}
						type="button"
						className={cn(
							"ms-input inline-flex shrink-0 items-center justify-center rounded-l-none border-l border-neutral-200 px-2.5",
							"focus:z-[1] focus:outline focus:outline-2 focus:-outline-offset-1",
							"data-popup-open:outline data-popup-open:outline-2 data-popup-open:-outline-offset-1",
							disabled && "cursor-not-allowed opacity-50",
						)}
						aria-label="Select date"
					>
						<CalendarIcon className="size-4 opacity-80" aria-hidden />
					</Popover.Trigger>
				</div>

				<Popover.Portal>
					<Popover.Positioner align="end" alignOffset={-8} className="z-1010" sideOffset={10}>
						<Popover.Popup
							className={cn(
								"w-auto overflow-hidden rounded-md border border-neutral-200 bg-white p-0 text-neutral-950 shadow-md outline-none",
								formVariantClassName,
							)}
						>
							<Calendar
								className="ms-form-date-picker-calendar"
								mode="single"
								captionLayout={showDropdowns ? "dropdown" : "label"}
								{...(monthBounds ? { startMonth: monthBounds.start, endMonth: monthBounds.end } : {})}
								month={month}
								onMonthChange={setMonth}
								selected={selected}
								onSelect={(date) => {
									onChange?.(date ? formatDateDmY(date) : "");
									setOpen(false);
								}}
								disabled={disabled}
							/>
						</Popover.Popup>
					</Popover.Positioner>
				</Popover.Portal>
			</Popover.Root>

			{description && <Description>{description}</Description>}
			<FieldError message={error} />
		</Field.Root>
	);
}

export default DatePickerInput;
